import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SettingsAction, SettingsRow } from '../../../types/businesses';
import Action from './Action';
import CopySection from './CopySection';

export const BORDER_RADIUS = '10px';
export const PADDING = 2;

export interface SettingsSectionProps {
  title?: string;
  subtitle?: string;
  rows: SettingsRow[];
  action?: SettingsAction;
  copy?: {
    content: string;
    onCopy?: () => void;
  };
  /**
   * If true, the labels will not be displayed and actions will be disabled
   */
  isLoading?: boolean;
}

function SettingsSection({
  rows,
  title,
  subtitle,
  action,
  isLoading,
  copy,
}: SettingsSectionProps) {
  return (
    <Stack gap={2} width="100%" boxSizing="border-box">
      {(!!title || !!subtitle || !!action) && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          boxSizing="border-box"
          gap={1}>
          <Stack gap="2px">
            <Typography variant="h3">{title}</Typography>
            {subtitle && <Typography variant="body2">{subtitle}</Typography>}
          </Stack>
          {action && <Action action={action} disabled={isLoading} />}
        </Box>
      )}

      <Stack
        width="100%"
        boxSizing="border-box"
        overflow="hidden"
        gap={2}
        borderRadius={BORDER_RADIUS}
        padding={PADDING}
        bgcolor={theme => theme.palette.lightGrey.main}>
        {rows?.map(({ action: rowAction, isLabelUserInput, ...row }) => (
          <Box
            key={'id' in row ? row.id : row.title}
            boxSizing="border-box"
            maxWidth="100%"
            overflow="auto"
            display="flex"
            alignItems="center"
            gap={2}
            justifyContent="space-between">
            {!('component' in row) && (
              <Stack maxWidth="100%" overflow="hidden">
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="h5">{row.title}</Typography>
                  {row.tooltip && (
                    <Tooltip
                      title={row.tooltip}
                      arrow
                      placement="top"
                      enterTouchDelay={0}>
                      <Button
                        variant="text"
                        color="inherit"
                        sx={{
                          padding: 0,
                          border: 'none',
                          width: 'auto',
                          minWidth: 0,
                          flexShrink: 1,
                          ':hover': {
                            backgroundColor: 'transparent',
                          },
                        }}>
                        <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                      </Button>
                    </Tooltip>
                  )}
                </Box>
                {typeof row.label === 'string' ? (
                  <Typography
                    variant="body2"
                    mt={0.5}
                    whiteSpace="pre-line"
                    sx={{
                      wordBreak: isLabelUserInput ? 'break-all' : 'normal',
                    }}>
                    {isLoading ? '-' : row.label}
                  </Typography>
                ) : (
                  row.label
                )}
              </Stack>
            )}
            {'component' in row && row.component}
            {rowAction && <Action action={rowAction} disabled={isLoading} />}
          </Box>
        ))}
      </Stack>

      {copy && <CopySection copy={copy} />}
    </Stack>
  );
}

export default SettingsSection;
